// Your profile page — avatar (upload/remove), name, username, and stats.
// Mirrors the self-facing half of iOS PublicProfileView. The richer settings
// (language, security, AI) stay on /app/settings.
import { useRef, useState, type ChangeEvent } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/auth/auth-context";
import { deleteAvatar, uploadAvatar } from "@/api/profile";
import { getPublicProfile } from "@/api/users";
import { Avatar } from "@/components/avatar";
import { Alert, Button, Spinner } from "@/components/ui";

export function ProfilePage() {
  const { t } = useTranslation();
  const { user, refreshMe } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);
  const [err, setErr] = useState<string | null>(null);

  const displayName = user?.display_name || user?.username || user?.email || t("You");
  const profileQuery = useQuery({
    queryKey: ["public-profile", user?.id],
    queryFn: () => getPublicProfile(user!.id),
    enabled: !!user?.id,
  });

  const upload = useMutation({
    mutationFn: (file: File) => uploadAvatar(file),
    onSuccess: async () => {
      setErr(null);
      await refreshMe();
    },
    onError: (e) => setErr(e instanceof Error ? e.message : t("Upload failed.")),
  });
  const remove = useMutation({
    mutationFn: () => deleteAvatar(),
    onSuccess: async () => {
      await refreshMe();
    },
  });

  const onPick = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) upload.mutate(file);
  };

  const p = profileQuery.data;

  return (
    <div className="mx-auto max-w-2xl animate-fade-up">
      <h1 className="text-3xl font-bold tracking-tight">{t("Profile")}</h1>

      <div className="card mt-5 flex items-center gap-5 p-5">
        <Avatar url={user?.avatar_url} name={displayName} size={80} />
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-xl font-bold">{displayName}</h2>
          {user?.username && <p className="text-sm text-ink-faint">@{user.username}</p>}
          <div className="mt-3 flex gap-2">
            <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onPick} />
            <Button size="sm" variant="secondary" loading={upload.isPending} onClick={() => inputRef.current?.click()}>
              {t("Change photo")}
            </Button>
            {user?.avatar_url && (
              <Button size="sm" variant="ghost" loading={remove.isPending} onClick={() => remove.mutate()}>
                {t("Remove")}
              </Button>
            )}
          </div>
        </div>
      </div>
      {err && <div className="mt-3"><Alert>{err}</Alert></div>}

      {profileQuery.isLoading && (
        <div className="flex justify-center py-10 text-brand-600">
          <Spinner className="h-6 w-6" />
        </div>
      )}

      {p && (
        <div className="mt-5 grid grid-cols-3 gap-3">
          <Stat label={t("Tier")} value={p.tier} />
          <Stat label={t("XP")} value={p.total_xp} />
          <Stat label={t("Streak")} value={p.current_streak} />
        </div>
      )}

      <p className="mt-6 text-sm text-ink-faint">
        {t("Manage language, security, and AI settings in")}{" "}
        <Link to="/app/settings" className="text-brand-600 hover:underline">
          {t("Settings")}
        </Link>
        .
      </p>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="card p-4 text-center">
      <div className="text-2xl font-bold text-ink">{value}</div>
      <div className="text-xs uppercase tracking-wide text-ink-faint">{label}</div>
    </div>
  );
}
